"use client";

import { updateGoogleConsent } from "@analytics/provider/google";
import { useEffect } from "react";

// IMPORTANT: the vanilla-cookieconsent CSS must be `@import`-ed from a bundled
// stylesheet (apps/web/globals.css), NOT lazy-loaded here — keeping it in the
// main stylesheet means it lands in CSS rather than the lazy JS chunk and the
// banner is styled on first paint. See globals.css.

// Cookie consent banner. Uses vanilla-cookieconsent for compliance
// scaffolding (GDPR copy, storage, geo-detection). The JS is imported
// dynamically to keep ~30KB out of the initial bundle.
//
// The banner is the only thing that flips Google Consent Mode v2 from the
// default "denied" state (set inline in __root.tsx / the analytics provider)
// to "granted". Every analytics event before the user accepts is anonymised
// automatically.
//
// Gated on VITE_GOOGLE_ANALYTICS_ID: with no analytics configured there is
// nothing to consent to, so the banner renders nothing.
const analyticsId = import.meta.env.VITE_GOOGLE_ANALYTICS_ID as
	| string
	| undefined;

export function ConsentBanner() {
	useEffect(() => {
		if (typeof window === "undefined" || !analyticsId) {
			return;
		}

		let cancelled = false;

		import("vanilla-cookieconsent").then((module) => {
			if (cancelled) {
				return;
			}

			module.run({
				categories: {
					necessary: { enabled: true, readOnly: true },
					analytics: {},
					marketing: {},
				},

				guiOptions: {
					consentModal: {
						layout: "box inline",
						position: "bottom right",
					},
					preferencesModal: {
						layout: "box",
						position: "right",
					},
				},

				onConsent: () => {
					updateGoogleConsent({
						analytics: module.acceptedCategory("analytics"),
						marketing: module.acceptedCategory("marketing"),
					});
				},

				onChange: () => {
					updateGoogleConsent({
						analytics: module.acceptedCategory("analytics"),
						marketing: module.acceptedCategory("marketing"),
					});
				},

				language: {
					default: "en",
					translations: {
						en: {
							consentModal: {
								title: "Cookies",
								description:
									"We use cookies to measure traffic and improve the experience.",
								acceptAllBtn: "Accept all",
								acceptNecessaryBtn: "Reject all",
								showPreferencesBtn: "Customize",
							},
							preferencesModal: {
								title: "Cookie preferences",
								acceptAllBtn: "Accept all",
								acceptNecessaryBtn: "Reject all",
								savePreferencesBtn: "Save",
								closeIconLabel: "Close",
								sections: [
									{
										title: "Strictly necessary",
										description:
											"Required for the site to function. Cannot be disabled.",
										linkedCategory: "necessary",
									},
									{
										title: "Analytics",
										description:
											"Measures traffic and usage. Helps us improve the product.",
										linkedCategory: "analytics",
									},
									{
										title: "Marketing",
										description:
											"Powers ad measurement and remarketing.",
										linkedCategory: "marketing",
									},
								],
							},
						},
					},
				},
			});
		});

		return () => {
			cancelled = true;
		};
	}, []);

	return null;
}
